import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {
  RefModelStoreService,
  SnackBarService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {BehaviorSubject} from 'rxjs';

import {CustomViewDaoService} from '../../../../../../core/services/custom-view-dao.service';
import {ICustomView, ICustomViewFilter} from '../../../../../models/custom-view.interface';
import {CustomViewService} from '../../../../../services/custom-view.service';
import {summaryInfoObjectValue} from '../../models/create-new-view.enum';

@Component({
  selector: 'app-edit-filters-dialog',
  styleUrls: ['./edit-filters-dialog.component.scss'],
  templateUrl: './edit-filters-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditFiltersDialogComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnDestroy
{
  public title = 'RUBIC_CUBE.EDIT_FILTERS_DIALOG_TITLE';
  public customView: ICustomView;
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<EditFiltersDialogComponent>,
    private customViewService: CustomViewService,
    private translate: TranslateService,
    private customViewDaoService: CustomViewDaoService,
    private rmeStore: RefModelStoreService,
    private snackBarService: SnackBarService
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this.data.view) {
      this.fetchCustomView();
    } else {
      this.handleCustomView(this.data.view);
    }
  }

  private handleCustomView(view: ICustomView) {
    this.customView = view;
    this.customViewService.setSelectedColumnFromCustomView(this.customView);

    this._parseFilters();
    this.loading$.next(false);
  }

  private fetchCustomView() {
    const refModel = this.rmeStore.refModel;
    this.customViewDaoService
      .getCustomView(refModel._id, this.data.viewId)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(
        response => {
          this.customView = response;
          this.customViewService.setSelectedColumnFromCustomView(this.customView);
          this._parseFilters();
          this.loading$.next(false);
        },
        error => {
          this.snackBarService.error(error.error.error);
          this.closeModal();
        }
      );
  }

  public submit($event) {
    const {refModel, _id, dynamicalView} = this.customView;

    const payload = $event.map((filter: ICustomViewFilter) => {
      if (filter.column.includes(summaryInfoObjectValue)) {
        return this.customViewService.createFilterObjectForSio(filter);
      }

      return filter;
    });

    if (dynamicalView) {
      this.dialogRef.close({filters: payload});

      return;
    }

    this.customViewDaoService.updateFilters(refModel, _id, payload).subscribe(
      response => {
        this.customViewService.updateCustomView(response);

        const msg = this.translate.instant('RUBIC_CUBE.EDITED_FILTERS_SUCCESSFULLY', {
          name: this.customView.name
        });

        this.dialogRef.close({data: response, msg});
      },
      error => {
        const msg = this.translate.instant(
          error.error.error || 'RUBIC_CUBE.EDITED_FILTERS_FAILURE',
          {
            name: this.customView.name
          }
        );

        this.dialogRef.close({msg});
      }
    );
  }

  public closeModal() {
    this.dialogRef.close();
  }

  private _parseFilters() {
    this.customView.filters = this.customView.filters.map(filter => {
      if (filter.column === summaryInfoObjectValue) {
        const {column, operator, subcolumn, value} = filter;

        return {
          column: `${column}.${subcolumn}`,
          operator,
          value
        };
      } else {
        return filter;
      }
    });
  }

  ngOnDestroy(): void {
    this.customViewService.resetModal();
  }
}
